import { useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import Arrow from './assets/arrow'
import {
  buildPhaseLoop,
  AnimationComponentProps as Props,
} from './phaseAnimation'

type Direction = 'up' | 'down' | 'left' | 'right'

const REFERENCE_SIZE = 24

/**
 * Offsets are multiples of the travel distance along the movement axis:
 * `-1` is the edge the arrow enters from, `+1` the edge it leaves through.
 * The last phase snaps back to the entry edge while invisible, so the loop
 * always restarts from step one instead of drifting the other way. Since that
 * phase is the reset, its value is also each property's starting state.
 */
const ARROW_OFFSET = [-1, 0, 1, 1]
const ARROW_OPACITY = [0, 1, 0, 0]
const ARROW_SCALE = [0.5, 1, 0.5, 0]

const resetValue = (values: readonly number[]) => values[values.length - 1]

const PHASE_DURATIONS = [500, 500, 250, 0]
const PHASE_DELAYS = [0, 0, 700, 0]
const PHASE_EASINGS = [
  Easing.linear,
  Easing.out(Easing.ease),
  Easing.in(Easing.ease),
  Easing.linear,
]

const ROTATION = {
  up: '180deg',
  down: '0deg',
  left: '90deg',
  right: '-90deg',
} as const satisfies Record<Direction, string>

const AXIS_X = {
  up: 0,
  down: 0,
  left: -1,
  right: 1,
} as const satisfies Record<Direction, number>

const AXIS_Y = {
  up: -1,
  down: 1,
  left: 0,
  right: 0,
} as const satisfies Record<Direction, number>

export type HeadDirectionProps = Props & {
  direction: Direction
}

export default function HeadDirection({
  direction,
  size = REFERENCE_SIZE,
  color = colors.white,
  durations = PHASE_DURATIONS,
  delays = PHASE_DELAYS,
  easings = PHASE_EASINGS,
  isActive = true,
}: HeadDirectionProps) {
  const half = size / 2
  const stageSize = size * 2
  const stepX = AXIS_X[direction] * half
  const stepY = AXIS_Y[direction] * half
  const axisX = Math.sign(AXIS_X[direction])
  const axisY = Math.sign(AXIS_Y[direction])

  const offset = useSharedValue(resetValue(ARROW_OFFSET))
  const opacity = useSharedValue(resetValue(ARROW_OPACITY))
  const scale = useSharedValue(resetValue(ARROW_SCALE))

  useEffect(() => {
    if (!isActive) {
      cancelAnimation(offset)
      cancelAnimation(opacity)
      cancelAnimation(scale)
      return
    }

    offset.set(buildPhaseLoop(ARROW_OFFSET, durations, delays, easings))
    opacity.set(buildPhaseLoop(ARROW_OPACITY, durations, delays, easings))
    scale.set(buildPhaseLoop(ARROW_SCALE, durations, delays, easings))
  }, [isActive, durations, delays, easings, offset, opacity, scale])

  const arrowStyle = useAnimatedStyle(() => {
    /**
     * The scale anchor follows the movement: while approaching the center the
     * arrow grows out of its trailing edge, once past it, it shrinks into its
     * leading edge. Scaling around an anchor `p` instead of the center is the
     * same as scaling around the center and translating by `p * (1 - scale)`,
     * which is what keeps that edge pinned. The flip happens where the arrow
     * reaches the center at full scale, so the compensation is 0 there and
     * nothing jumps.
     */
    const anchor = offset.value > 0 ? half : -half
    const pinned = anchor * (1 - scale.value)

    return {
      opacity: opacity.value,
      transform: [
        { translateX: offset.value * stepX + pinned * axisX },
        { translateY: offset.value * stepY + pinned * axisY },
        { scale: scale.value },
      ],
    }
  })

  return (
    <View
      style={{ width: stageSize, height: stageSize }}
      className="items-center justify-center"
    >
      <Animated.View style={[{ width: size, height: size }, arrowStyle]}>
        <View
          style={{
            width: size,
            height: size,
            transform: [{ rotate: ROTATION[direction] }],
          }}
        >
          <Arrow size="100%" color={color} />
        </View>
      </Animated.View>
    </View>
  )
}
